import { Component, Optional, ElementRef, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { CommunicationDTO, ProjectEntityDTO, CommunicationStatusType } from '../../api/plyca-dtos-v2';
import { ActivatedRoute } from '@angular/router';
import { TaskHandler } from '../../tasks/TaskHandler';
import { Configuration } from '../../conf/app.constants';
import { ALL_ELEMENTS_SIZE, PaginationDTO } from '../../api/PaginationModel';
import { HeaderActionsService } from '../../components/common/sub-header/header-actions.service';
import { forkJoin, Observable } from 'rxjs';
import { CommunicationEditionComponent } from '../communications/communication.edition.component';
import { tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { HelpService, HelpType } from '../../components/common/help/help.service';
import { DynamicDialogConfig } from 'primeng-lts/dynamicdialog';

enum QuestionState {
  PENDING,
  EDITING,
  SENDING,
  SENDED
};

class ProjectQuestionBean {
  question: CommunicationDTO;
  response: CommunicationDTO;

  get status(): QuestionState {
    let state: QuestionState;
    if (this.response) {
      if (this.response.status.type === CommunicationStatusType.SENDING) {
        state = QuestionState.SENDING;
      } else if (this.response.status.type === CommunicationStatusType.SENDED) {
        state = QuestionState.SENDED;
      } else {
        state = QuestionState.EDITING;
      }
    } else {
      state = QuestionState.PENDING;
    }
    return state;
  }

}

@Component({
  selector: 'project-questions',
  templateUrl: 'project-questions.template.html'
})
export class ProjectQuestionsComponent extends TaskHandler implements OnInit, OnDestroy, AfterViewInit {

  public projectId: string;
  public questions: Array<ProjectQuestionBean>;
  public loaded: boolean;
  public activeTabs: number[];
  public QuestionState = QuestionState;

  constructor(protected elem: ElementRef,
    protected configuration: Configuration,
    protected headerActionsService: HeaderActionsService,
    protected helpService: HelpService,
    @Optional() protected activatedRoute: ActivatedRoute) {

    super();
    this.loaded = false;
    this.subscribe(activatedRoute.params, params => {
      this.projectId = params['id'];
    });
  }

  ngAfterViewInit(): void {
    this.subscribe(this.helpService.showContextHelpObservable(HelpType.BIDDER_QUESTIONS));
  }

  ngOnInit(): void {
    this.waitService.startWaiting();
    this.subscribe(forkJoin(
      this.plycaService.get<ProjectEntityDTO>(
        this.configuration.PROJECTENTITY_SERVICE_URL,
        this.projectId,
        this.getProjectEntityQueryParams()
      ),
      this.loadQuestions().pipe(
        tap((result: PaginationDTO<CommunicationDTO>) => this.initTabIndex())
      )
    ), (result: any) => {
      if (result) {
        let value: any;
        value = result[0] as ProjectEntityDTO;
        if (value) {
          this.headerActionsService.clearActions();
          this.headerActionsService.setProject(this.elem, value, false);
          this.headerActionsService.setTaskDescription(
            this.elem,
            this.translateService.instant('ProjectQuestions.title'),
            'far fa-question-circle',
            true
          );
          this.subscribe(this.headerActionsService.addAction(
            'General.reload',
            'fas fa-lg fa-sync-alt',
            'btn-primary',
            true
          ), () => {
            this.reload();
          });
          this.headerActionsService.addDivider();
          this.subscribe(this.headerActionsService.addAction(
            'General.back',
            'fas fa-chevron-left',
            'btn-outline-secondary',
            true
          ), success => {
            this.salir();
          });
        }
      }
    },
      () => undefined,
      () => {
        this.loaded = true;
        this.waitService.cancelWaiting();
      });
  }

  ngOnDestroy() {
    this.headerActionsService.clearActions();
    super.ngOnDestroy();
  }

  public reload(): void {
    this.waitService.startWaiting();
    this.subscribe(this.loadQuestions(), () => undefined,
      () => undefined,
      () => this.waitService.cancelWaiting())
      ;
  }

  public getNumberOfNotAnswered(): number {
    return this.questions.filter((question: ProjectQuestionBean) =>
      question.status !== QuestionState.SENDED && question.status !== QuestionState.SENDING).length;
  }

  public editQuestion(questionBean: ProjectQuestionBean): void {
    this.edit(questionBean, questionBean.status === QuestionState.PENDING);
  }


  private loadQuestions(): Observable<any> {
    return this.plycaService.query<CommunicationDTO>(this.configuration.COMMUNICATIONS_SERVICE_URL, this.getQuestionsQueryParams())
      .pipe(
        tap((result: PaginationDTO<CommunicationDTO>) => this.processCommunications(result))
      );
  }

  private edit(questionBean: ProjectQuestionBean, newReply: boolean): void {
    let config: DynamicDialogConfig = {
      header: this.translateService.instant('Communications.detail.form.title'),
      width: '70%',
      styleClass: 'formEdition'
    };
    config.data = {
      'communication': (newReply) ? questionBean.question : questionBean.response,
      'reply': newReply,
      'edition': true
    }
    // this.editDialogOpened.emit(true);
    let ref = this.dialogService.open(CommunicationEditionComponent, config);
    this.subscribe(ref.onClose, (result: any) => {
      if (result) {
        this.reload();
      }
    });
  }


  private processCommunications(communications: PaginationDTO<CommunicationDTO>): void {
    let questions = new Array<ProjectQuestionBean>();
    // ordenar comunicaciones
    if (communications && communications.content) {
      let queryResponseList = communications.content;
      queryResponseList.filter((comm: CommunicationDTO) => !comm.repliedCommunication)
        .forEach((comm: CommunicationDTO) => {
          questions.push(this.toBean(comm))
        });
      queryResponseList.filter((comm: CommunicationDTO) => comm.repliedCommunication)
        .forEach((comm: CommunicationDTO) => {
          let parentComm = questions.find((questionBean: ProjectQuestionBean) =>
            questionBean.question.communicationUUID === comm.repliedCommunication.communicationUUID);
          if (parentComm) {
            parentComm.response = comm;
          }
        });
    }
    this.questions = questions;
  }

  private toBean(comm: CommunicationDTO): ProjectQuestionBean {
    let bean = new ProjectQuestionBean();
    bean.question = comm;
    if (comm.responses) {
      bean.response = comm.responses[comm.responses.length - 1];
    }
    return bean;
  }

  private getProjectEntityQueryParams(): { [param: string]: string | string[]; } {
    return {
      expand: 'expansionSet:minimal'
    };
  }

  private getQuestionsQueryParams(): { [param: string]: string | string[]; } {
    return {
      projectId: this.projectId,
      activityIds: 'PREGUNTA',
      deleted: 'false',
      expand: 'expansionSet:web.collection',
      page: '0',
      size: ALL_ELEMENTS_SIZE,
      sort: 'creationDate,desc'
    };
  }

  private initTabIndex(): void {
    // marcar pestaña activa aquellas preguntas no respondidas
    let activeTabs = [];
    this.questions.forEach((questionBean: ProjectQuestionBean, index: number) => {
      activeTabs.push(index);
    })
    this.activeTabs = activeTabs;
  }

}